import { useEffect, useState } from 'react';
import { CheckCircle, XCircle, Clock, ArrowLeft, AlertCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useRouter } from '../components/Router';
import { getListingData, clearListingData } from '../utils/listingStorage';

export function AuthCallbackPage() {
  const { navigate } = useRouter();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Vérification en cours...');
  const [redirectTo, setRedirectTo] = useState('/');

  useEffect(() => {
    handleAuthCallback();
  }, []);

  const handleAuthCallback = async () => {
    try {
      // Get redirect parameter from URL
      const params = new URLSearchParams(window.location.search);
      const redirectUrl = params.get('redirect');
      if (redirectUrl) {
        setRedirectTo(redirectUrl);
      }

      // Check for error in hash
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const errorCode = hashParams.get('error_code');
      const errorDescription = hashParams.get('error_description');

      if (errorCode) {
        setStatus('error');
        let errorMessage = 'Une erreur est survenue lors de la vérification.';

        if (errorCode === 'otp_expired') {
          errorMessage = 'Le lien de vérification a expiré. Veuillez vous réinscrire.';
        } else if (errorCode === 'access_denied') {
          errorMessage = 'Accès refusé. Veuillez vérifier le lien.';
        } else if (errorDescription) {
          errorMessage = errorDescription;
        }

        setMessage(errorMessage);
        return;
      }

      // Get current session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();

      if (sessionError || !session) {
        setStatus('error');
        setMessage('Session invalide. Veuillez vous reconnecter.');
        return;
      }

      // Check if there's a pending listing to submit
      const pendingListing = getListingData();
      
      if (pendingListing && redirectUrl === '/soumettre-annonce') {
        setMessage('Email vérifié. Publication de votre annonce en cours...');
        
        try {
          // Ensure user profile exists
          const { error: profileError } = await supabase
            .from('user_profiles')
            .upsert({ user_id: session.user.id }, { onConflict: 'user_id' });

          if (profileError) {
            console.error('Profile creation error:', profileError);
          }

          // Submit the pending listing
          const { error: listingError } = await supabase
            .from('listings')
            .insert([
              {
                user_id: session.user.id,
                brand: pendingListing.brand,
                model: pendingListing.model,
                year: parseInt(pendingListing.year),
                mileage: parseInt(pendingListing.mileage),
                fuel_type: pendingListing.fuelType,
                transmission: pendingListing.transmission,
                color: pendingListing.color,
                price: parseFloat(pendingListing.price),
                description: pendingListing.description,
                location: pendingListing.location,
                condition: pendingListing.condition,
                contact_phone: pendingListing.contactPhone,
                contact_email: pendingListing.contactEmail,
                images: pendingListing.imageUrls || [],
                status: 'pending'
              }
            ]);

          if (listingError) {
            console.error('Listing submission error:', listingError);
            setStatus('error');
            setMessage('Email vérifié, mais erreur lors de la publication. Veuillez réessayer depuis Mon Garage.');
            setTimeout(() => navigate('/soumettre-annonce'), 3000);
            return;
          }

          // Clear the saved listing data
          clearListingData();
          
          setStatus('success');
          setMessage('Email vérifié et annonce publiée avec succès !');
          
          // Redirect to my listings
          setTimeout(() => navigate('/mes-annonces'), 3000);
        } catch (err: any) {
          console.error('Error during listing submission:', err);
          setStatus('error');
          setMessage('Email vérifié, mais erreur lors de la publication. Veuillez réessayer.');
          setTimeout(() => navigate('/soumettre-annonce'), 3000);
        }
      } else {
        // No pending listing, just confirm verification
        setStatus('success');
        setMessage('Email vérifié avec succès !');
        
        // Redirect to original destination or home
        setTimeout(() => navigate(redirectUrl || '/'), 2000);
      }
    } catch (err: any) {
      console.error('Auth callback error:', err);
      setStatus('error');
      setMessage('Une erreur est survenue. Veuillez réessayer.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-white rounded-3xl shadow-xl p-10 text-center animate-in fade-in duration-500">
        
        {status === 'loading' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#D4452C] border-t-transparent mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">Vérification en cours</h2>
            <p className="text-gray-500 text-base">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-green-200">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">Succès !</h2>
            <p className="text-gray-600 mb-8 font-medium leading-relaxed">{message}</p>
            
            <div className="inline-flex items-center text-sm font-semibold text-gray-500 bg-gray-50 px-4 py-2 rounded-full">
              <Clock className="h-4 w-4 mr-2" />
              Redirection en cours...
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-red-200">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3 tracking-tight">Échec de la vérification</h2>
            <p className="text-gray-600 mb-8 font-medium leading-relaxed">{message}</p>
            
            <div className="space-y-3">
              <a
                href="/registre"
                className="inline-block w-full bg-[#D4452C] text-white py-3.5 rounded-xl font-bold hover:bg-[#b83a24] shadow-lg shadow-orange-900/10 transition-all"
              >
                Réessayer de s'inscrire
              </a>
              <a
                href="/connecter"
                className="inline-block w-full bg-gray-100 text-gray-700 py-3.5 rounded-xl font-bold hover:bg-gray-200 transition-all"
              >
                Se connecter
              </a>
            </div>
          </>
        )}
        
        <div className="mt-8 pt-6 border-t border-gray-100">
          <a 
            href="/" 
            className="flex items-center justify-center text-sm font-semibold text-gray-500 hover:text-[#D4452C] transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Retour à l'accueil
          </a>
        </div>
      </div>
    </div>
  );
}